import React from 'react';
import splitFilename from '../../utils/splitFilename';
import { urlReadDoc } from '../../urls';
import { CompressedFileTreeProviderOptions } from './viewers/common/compressed';
import { LayerToolOptions } from './viewers/common/layer/layerTool';
import { ImageMetadataProviderOptions } from './viewers/common/image';
import * as icons from './viewers/common/Icons';
export type FileMetadata = {
  duration?: number;
  height?: number;
  width?: number;
};

export enum MediaType {
  Audio = 'Audio',
  Image = 'Image',
  ImageTGA = 'ImageTGA',
  Video = 'Video',
  Asset3D = 'Asset3D',
  ImageVector = 'ImageVector',
  IFrame = 'IFrame',
  Compressed = 'Compressed',
  Text = 'Text',
  Docx = 'Docx',
  // Spine-анимация (json скелета + atlas + png рядом). Не основной тип ни одного расширения:
  // доступен как альтернативный просмотрщик json (MYUA-17117).
  Spine = 'Spine',
  UNKNOWN = 'UNKNOWN',
}

export const getExtension = (filename: string) => {
  return splitFilename(filename).extension.toLowerCase();
};

export const getDefinition = (filename: string): ExtensionDefinition => {
  for (const [extension, definition] of extensionsDefinitions.entries()) {
    if (filename.toLowerCase().endsWith('.' + extension.toLowerCase())) {
      return definition;
    }
  }
  return {
    mt: MediaType.UNKNOWN,
    streamable: false,
    thumbnail: false,
    hasLayerTool: false,
    customIcon: undefined,
    extension: getExtension(filename),
  };
};

export const getFileViewerLink = (
  filename: string,
  downloadLink: string,
  streamingLink: string,
) => {
  const definition = getDefinition(filename);
  const url = getFileViewerLinkByDefinition(definition, downloadLink, streamingLink);
  return url;
};

const extractPreviewSourceIdFromLink = (link: string): number | null => {
  try {
    const url = new URL(link, window.location.origin);
    const nodeId = url.searchParams.get('node_id');

    if (nodeId !== null) {
      const parsedNodeId = Number.parseInt(nodeId, 10);
      return Number.isNaN(parsedNodeId) ? null : parsedNodeId;
    }

    const assetIds = url.searchParams.get('asset_ids');
    if (assetIds === null || assetIds.includes(',')) {
      return null;
    }

    const parsedAssetId = Number.parseInt(assetIds, 10);
    return Number.isNaN(parsedAssetId) ? null : parsedAssetId;
  } catch {
    return null;
  }
};

export const getFileViewerLinkByDefinition = (
  definition: ExtensionDefinition,
  downloadLink: string,
  streamingLink: string,
): string => {
  if (definition.mt === MediaType.Docx) {
    const previewSourceId = extractPreviewSourceIdFromLink(downloadLink);

    if (previewSourceId !== null) {
      return getFileViewerLinkByDefinition(definition, urlReadDoc(previewSourceId), streamingLink);
    }
  }

  let url = definition.streamable ? streamingLink : downloadLink;

  if (document.location.hostname === 'localhost') {
    //replace host to current host
    url = url.replace(/https?:\/\/[^/]+/, document.location.origin);
  }

  return url;
};

export type ExtensionDefinition = {
  mt: MediaType;
  streamable: boolean;
  thumbnail: boolean;
  hasLayerTool: boolean;
  customIcon?: React.FC<{ className?: string }>;
  extension: string;
  /** Просмотрщики, на которые пользователь может переключиться вместо mt (MYUA-17117). */
  alternativeViewers?: MediaType[];
};

/**
 *
 * @param filename имя файла
 * @param useDefaultIcon в случае, если иконки для данного типа файлов нет, возвращать defaultFileIcon или undefined
 * @returns
 */
export const getIcon = (filename: string, useDefaultIcon: boolean) => {
  const def = getDefinition(filename);
  return def.customIcon ?? (useDefaultIcon ? icons.DefaultFileIcon : undefined);
};

/**
 * create definition
 */
function def(
  extension: string,
  mediaType: MediaType,
  traits: ('streamable' | 'thumbnail' | 'hasLayerTool')[],
  customIcon?: React.FC<{ className?: string }>,
  extra?: { alternativeViewers?: MediaType[] },
): [string, ExtensionDefinition] {
  return [
    extension,
    {
      mt: mediaType,
      streamable: traits.includes('streamable'),
      thumbnail: traits.includes('thumbnail'),
      hasLayerTool: traits.includes('hasLayerTool'),
      customIcon,
      extension,
      ...(extra?.alternativeViewers ? { alternativeViewers: extra.alternativeViewers } : {}),
    },
  ];
}

const extensionsDefinitions = new Map<string, ExtensionDefinition>([
  /**
   * Images
   */
  def('png', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('jpg', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('jpeg', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('gif', MediaType.Image, ['thumbnail'], icons.IconImage),
  def('bmp', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('svg', MediaType.ImageVector, ['thumbnail'], icons.IconVector),
  def('eps', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('tga', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('tiff', MediaType.Image, ['streamable', 'thumbnail'], icons.IconImage),
  def('webp', MediaType.Image, ['thumbnail'], icons.IconImage),
  // Special case: .blend files are 3d, but we preview them as images
  def('blend', MediaType.Image, ['thumbnail', 'streamable'], icons.IconBlenderSoftware),
  /**
   * Audio
   */

  def('mp3', MediaType.Audio, ['streamable'], icons.IconAudio),
  def('wav', MediaType.Audio, ['streamable'], icons.IconAudio),
  // macos: Supports the Vorbis audio codec, but not the Ogg container.
  def('ogg', MediaType.Audio, [], icons.IconAudio),
  // AAC: caniuse: Partial support in Firefox refers to only supporting AAC in an
  // MP4 container and only when the operating system already has the codecs
  // installed.
  def('aac', MediaType.Audio, ['streamable'], icons.IconAudio),
  /**
   * Video
   */
  def('mp4', MediaType.Video, ['streamable', 'thumbnail'], icons.IconVideo),
  def('mov', MediaType.Video, ['streamable', 'thumbnail'], icons.IconVideo),
  def('avi', MediaType.Video, ['streamable', 'thumbnail'], icons.IconVideo),
  def('webm', MediaType.Video, ['streamable', 'thumbnail'], icons.IconVideo),
  /**
   * 3D
   */
  def('fbx', MediaType.Asset3D, ['thumbnail'], icons.Icon3D),
  def('obj', MediaType.Asset3D, ['thumbnail'], icons.Icon3D),
  /**
   * Text
   */
  def('txt', MediaType.Text, [], icons.IconText),
  def('ini', MediaType.Text, [], icons.IconCog),
  def('json', MediaType.Text, [], icons.IconJSON, { alternativeViewers: [MediaType.Spine] }),
  def('bat', MediaType.Text, [], icons.IconConsole),
  def('overridecontroller', MediaType.Text, [], icons.IconUnity),
  def('sh', MediaType.Text, [], icons.IconConsole),
  /**
   * Compressed
   */
  def('zip', MediaType.Compressed, ['thumbnail'], icons.IconCompressed),
  def('tar', MediaType.Compressed, [], icons.IconCompressed),
  def('gz', MediaType.Compressed, [], icons.IconCompressed),
  def('tgz', MediaType.Compressed, [], icons.IconCompressed),
  def('rar', MediaType.Compressed, ['thumbnail'], icons.IconCompressed),
  def('7z', MediaType.Compressed, ['thumbnail'], icons.IconCompressed),
  /**
   * Everything else
   */
  // Для psd, psb задан MediaType.Image, чтобы отображались превью в хабе.
  // Если бы везде перешли на конструкцию preview лаба, то лучше было бы задать MediaType.UNKNOWN.
  // Тогда бы при отсутствии превью просто отображалось название файла,
  // и не делалась бы попытка отрендерить картинку (не показывалась бы ошибка загрузки изображения).
  def('psd', MediaType.Image, ['streamable', 'thumbnail', 'hasLayerTool'], icons.IconImage),
  def('psb', MediaType.Image, ['streamable', 'thumbnail', 'hasLayerTool'], icons.IconImage),

  def('ai', MediaType.IFrame, [], icons.IconVector),
  def('html', MediaType.IFrame, ['thumbnail'], icons.IconWeb),
  def('pdf', MediaType.IFrame, [], icons.IconPDF),
  /**
   * Everything else, but we don't have previewers for these files
   */
  def('spine', MediaType.UNKNOWN, [], icons.IconSpineAlternate),
  def('ts', MediaType.UNKNOWN, [], icons.IconVideo),
  // Unity
  def('unity', MediaType.UNKNOWN, [], icons.IconUnity),
  def('prefab', MediaType.UNKNOWN, [], icons.IconUnity),
  def('controller', MediaType.UNKNOWN, [], icons.IconUnity),
  def('anim', MediaType.UNKNOWN, [], icons.IconUnity),
  def('playable', MediaType.UNKNOWN, [], icons.IconUnity),
  def('mat', MediaType.UNKNOWN, [], icons.IconUnity),
  def('mask', MediaType.UNKNOWN, [], icons.IconUnity),
  def('unitypackage', MediaType.UNKNOWN, [], icons.IconUnity),

  def('icloud', MediaType.UNKNOWN, [], icons.IconAppleICloud),
  def('DS_Store', MediaType.UNKNOWN, [], icons.IconAppleFinder),
  def('ttf', MediaType.UNKNOWN, [], icons.IconFont),
  def('otf', MediaType.UNKNOWN, [], icons.IconFont),
  def('tif', MediaType.UNKNOWN, [], icons.IconTexture),
  def('iff', MediaType.UNKNOWN, [], icons.IconTexture),
  def('hdr', MediaType.UNKNOWN, [], icons.IconTexture),
  def('exr', MediaType.UNKNOWN, [], icons.IconTexture),
  def('docx', MediaType.Docx, [], icons.IconMicrosoftWord),
  def('doc', MediaType.UNKNOWN, [], icons.IconMicrosoftWord),
  def('swf', MediaType.UNKNOWN, [], icons.IconAdobeFlash),
  def('fla', MediaType.UNKNOWN, [], icons.IconAdobeFlash),
  def('max', MediaType.UNKNOWN, [], icons.Icon3D),
  def('mtl', MediaType.UNKNOWN, [], icons.Icon3D),
  def('3b', MediaType.UNKNOWN, [], icons.Icon3D),
  def('c4d', MediaType.UNKNOWN, [], icons.Icon3D),
  def('ma', MediaType.UNKNOWN, [], icons.Icon3D),
  def('mb', MediaType.UNKNOWN, [], icons.Icon3D),
  def('swatch', MediaType.UNKNOWN, [], icons.IconSwatch),
  def('swatches', MediaType.UNKNOWN, [], icons.IconSwatch),
  def('skel.bytes', MediaType.UNKNOWN, [], icons.IconSpineAlternate),
  def('gltf', MediaType.UNKNOWN, [], icons.Icon3D),
  def('uasset', MediaType.UNKNOWN, [], icons.IconUnreal),
  def('csv', MediaType.Text, [], icons.IconCSV),
]);

export type ViewerProperties = {
  src: string;
  filename: string;
  type: MediaType;
  captureMode?: boolean;
  attachEventsToDocument?: boolean;
  invert?: boolean;
  compact?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  onMetadataAvailable: (meta: FileMetadata) => void;
  layerTool: LayerToolOptions | undefined;
  compressedFileTreeProvider: CompressedFileTreeProviderOptions | null;
  imageMetadataProvider: ImageMetadataProviderOptions | null;
};

export type ViewerPropertiesComplete = Required<ViewerProperties>;

export const ThemeContext = React.createContext({ invert: false });
